/**
 * Consent / Cookie-Banner — Typen und Konstanten.
 *
 * Bei rechtlich relevanten Änderungen (neue Dienste, neue Kategorien,
 * geänderte Zwecke) MUSS CONSENT_VERSION erhöht werden — dadurch wird die
 * Einwilligung erneut eingeholt.
 */

export const CONSENT_VERSION = 4;
export const CONSENT_STORAGE_KEY = "creategy.consent.v1";
export const CONSENT_COOKIE_NAME = "creategy_consent";
export const CONSENT_MAX_AGE_DAYS = 365;

export type ConsentCategory = "essential" | "functional" | "statistics" | "marketing";

export type ConsentCategories = Record<ConsentCategory, boolean>;

export interface ConsentState {
  version: number;
  decidedAt: string; // ISO timestamp
  categories: ConsentCategories;
}

export const DEFAULT_CATEGORIES: ConsentCategories = {
  essential: true,
  functional: false,
  statistics: false,
  marketing: false,
};

export const ALL_GRANTED: ConsentCategories = {
  essential: true,
  functional: true,
  statistics: true,
  marketing: true,
};

export interface ConsentServiceInfo {
  id: string;
  name: string;
  provider: string;
  purpose: string;
  retention: string;
  legalBasis: string;
  thirdCountry?: string;
}

export const CATEGORY_META: Record<
  ConsentCategory,
  { title: string; description: string; required?: boolean; services: ConsentServiceInfo[] }
> = {
  essential: {
    title: "Essenziell",
    description:
      "Technisch notwendige Dienste, ohne die unsere Website nicht sicher betrieben werden kann. Rechtsgrundlage: § 25 Abs. 2 Nr. 2 TDDDG i. V. m. Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an einem funktionsfähigen, sicheren Webangebot).",
    required: true,
    services: [
      {
        id: "consent",
        name: "Consent-Cookie",
        provider: "creategy GmbH, Berlin",
        purpose:
          "Speichert Ihre Cookie-Entscheidung, damit der Banner nicht bei jedem Aufruf erneut erscheint.",
        retention: "12 Monate",
        legalBasis: "§ 25 Abs. 2 Nr. 2 TDDDG",
      },
      {
        id: "supabase-session",
        name: "Supabase Auth-Session",
        provider: "Supabase Inc., 970 Toa Payoh North, Singapur (EU-Region Frankfurt)",
        purpose:
          "Sichere Anmeldung an internen Bereichen (Redaktion, Admin). Setzt nur nach Login lokale Speicherwerte und ein Session-Cookie.",
        retention: "Sitzung, max. 30 Tage",
        legalBasis: "Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung/interner Zugang)",
      },
      {
        id: "cloudflare-turnstile",
        name: "Cloudflare Turnstile",
        provider: "Cloudflare, Inc., 101 Townsend St, San Francisco, USA",
        purpose:
          "Bot- und Spam-Schutz für Kontakt- und Login-Formulare (Captcha-Ersatz). Verarbeitet IP-Adresse und Browsersignale.",
        retention: "Bis zu 30 Minuten (__cf_bm)",
        legalBasis:
          "§ 25 Abs. 2 Nr. 2 TDDDG i. V. m. Art. 6 Abs. 1 lit. f DSGVO (Schutz vor Missbrauch)",
        thirdCountry:
          "USA – EU-U.S. Data Privacy Framework (DPF) zertifiziert; ergänzend Standardvertragsklauseln",
      },
      {
        id: "cloudflare-cdn",
        name: "Cloudflare CDN & DDoS-Schutz",
        provider: "Cloudflare, Inc., San Francisco, USA",
        purpose:
          "Auslieferung der Website über ein europäisches Edge-Netz, TLS-Terminierung und Abwehr von Angriffen. Verarbeitet IP-Adresse und Verbindungsmetadaten.",
        retention: "Server-Logs bis zu 7 Tage",
        legalBasis: "Art. 6 Abs. 1 lit. f DSGVO (sicherer Betrieb)",
        thirdCountry: "USA – DPF-zertifiziert, SCC",
      },
    ],
  },
  functional: {
    title: "Funktional (Komfort & Einbettungen)",
    description:
      "Ermöglicht Video-, Karten- und Buchungs-Einbettungen sowie den Bezug von Schriftarten. Ohne Einwilligung sehen Sie an diesen Stellen einen Klick-zum-Laden-Platzhalter oder eine Text-Alternative. Rechtsgrundlage: Art. 6 Abs. 1 lit. a DSGVO, § 25 Abs. 1 TDDDG.",
    services: [
      {
        id: "vimeo",
        name: "Vimeo (Video-Player)",
        provider: "Vimeo.com, Inc., 555 West 18th Street, New York, USA",
        purpose:
          "Wiedergabe eingebetteter Videos. Wir laden Vimeo mit dem Do-Not-Track-Parameter (dnt=1); dennoch werden IP-Adresse und Geräteinformationen an Vimeo übermittelt.",
        retention: "Siehe Vimeo Privacy Policy",
        legalBasis: "Art. 6 Abs. 1 lit. a DSGVO",
        thirdCountry: "USA – SCC; DPF-Zertifizierung des Anbieters",
      },
      {
        id: "youtube-nocookie",
        name: "YouTube (erweiterter Datenschutzmodus)",
        provider: "Google Ireland Ltd., Gordon House, Barrow Street, Dublin 4, Irland",
        purpose:
          "Wiedergabe eingebetteter Videos über youtube-nocookie.com. Cookies werden erst bei Interaktion gesetzt; IP-Adresse wird an Google übermittelt.",
        retention: "Siehe Google Privacy Policy",
        legalBasis: "Art. 6 Abs. 1 lit. a DSGVO",
        thirdCountry: "USA – DPF-zertifiziert, SCC",
      },
      {
        id: "zeeg",
        name: "Zeeg (Terminbuchung)",
        provider: "Zeeg GmbH, Berlin, Deutschland",
        purpose:
          "Online-Terminvereinbarung mit unseren Ansprechpartner:innen. Verarbeitet Name, E-Mail, Terminwunsch sowie technische Metadaten.",
        retention: "Bis Widerruf / Vertragsende",
        legalBasis: "Art. 6 Abs. 1 lit. b DSGVO (Anbahnung), Art. 6 Abs. 1 lit. a DSGVO (Cookies)",
      },
    ],
  },
  statistics: {
    title: "Statistik & Analyse",
    description:
      "Hilft uns zu verstehen, wie Besucher unsere Website nutzen, damit wir Inhalte, Struktur und Performance verbessern können. Rechtsgrundlage: Art. 6 Abs. 1 lit. a DSGVO, § 25 Abs. 1 TDDDG.",
    services: [
      {
        id: "ga4",
        name: "Google Analytics 4",
        provider: "Google Ireland Ltd., Dublin, Irland",
        purpose:
          "Pseudonyme Reichweitenmessung und Nutzungsanalyse. IP-Anonymisierung ist aktiv; Google Signals ist deaktiviert.",
        retention: "Bis zu 14 Monate",
        legalBasis: "Art. 6 Abs. 1 lit. a DSGVO",
        thirdCountry: "USA – DPF-zertifiziert, SCC",
      },
      {
        id: "mouseflow",
        name: "Mouseflow",
        provider: "Mouseflow ApS, Flæsketorvet 68, 1711 Kopenhagen, Dänemark",
        purpose:
          "Anonymisierte Heatmaps und Session-Recordings zur UX-Optimierung. Formularinhalte und interne Bereiche sind vom Recording ausgeschlossen.",
        retention: "Bis zu 6 Monate",
        legalBasis: "Art. 6 Abs. 1 lit. a DSGVO",
      },
      {
        id: "web-vitals",
        name: "Web-Vitals-Messung",
        provider: "creategy GmbH (eigene Datenbank auf Supabase, EU-Region Frankfurt)",
        purpose:
          "Erfassung anonymer Kennzahlen zur Ladezeit (LCP, CLS, INP) zur laufenden Performance-Verbesserung. Keine Personenbezugsdaten, kein Cross-Site-Tracking.",
        retention: "90 Tage",
        legalBasis: "Art. 6 Abs. 1 lit. a DSGVO",
      },
    ],
  },
  marketing: {
    title: "Marketing",
    description:
      "Wird verwendet, um Werbeanzeigen relevanter zu gestalten und den Erfolg unserer Kampagnen zu messen. Rechtsgrundlage: Art. 6 Abs. 1 lit. a DSGVO, § 25 Abs. 1 TDDDG.",
    services: [
      {
        id: "google-ads",
        name: "Google Ads (Conversion-Tracking & Remarketing)",
        provider: "Google Ireland Ltd., Dublin, Irland",
        purpose:
          "Messung von Anzeigen-Conversions und Auslieferung interessenbezogener Werbung auf Google-Werbeflächen und im Displaynetzwerk.",
        retention: "Bis zu 540 Tage",
        legalBasis: "Art. 6 Abs. 1 lit. a DSGVO",
        thirdCountry: "USA – DPF-zertifiziert, SCC",
      },
      {
        id: "chatgpt-ads",
        name: "ChatGPT-Ads-Pixel (OpenAI)",
        provider: "OpenAI OpCo, LLC, San Francisco, USA",
        purpose:
          "Messung von Conversions (z. B. gebuchte Termine) aus Werbeanzeigen in ChatGPT.",
        retention: "Bis zum Widerruf der Einwilligung",
        legalBasis: "Art. 6 Abs. 1 lit. a DSGVO, § 25 Abs. 1 TDDDG",
        thirdCountry: "USA – EU-Standardvertragsklauseln",
      },
    ],
  },
};

export const CATEGORY_ORDER: ConsentCategory[] = [
  "essential",
  "functional",
  "statistics",
  "marketing",
];
